/**
 * CREAR LA FICHA DE OPERACION DESDE LA FICHA UNICA (SUJ-T13 punto 2).
 *
 * Una persona con llave de panel en un negocio y SIN ficha de operacion ahi (sin PIN, no marca, no entra al Desktop 1.5)
 * recibe su ficha en UN paso: POST /api/v1/personas/{personaId}/fichas-de-operacion (nexo-api 647, servido en 1.6.884).
 * La API crea la ficha colgada de ESA persona; componerlo con alta + unir dejaba una persona duplicada (#37134).
 *
 * EL CARGO es obligatorio y no se precarga (Q #37151, Ares #37156): el escritorio da rol y permisos por el cargo, y
 * Propietario o uno de sus sinonimos lo vuelve dueno con permisos libres, y Administrador le da rol de administrador. Esos
 * cargos solo los asigna el dueno o el admin: la API responde 403 CARGO_RESERVADO. El panel avisa antes, pero decide la API.
 *
 * Sin DOM: lo prueba scripts/check-ficha-de-operacion.mjs.
 */

const texto = (v) => (v == null ? '' : String(v));

/**
 * Copia de CargosReservados.RolDesdeCargo (nexo-api 648, la misma del escritorio): Trim + minusculas e igualdad exacta.
 * Reservado = el escritorio le da un rol distinto de Empleado; la API lo frena con 403 CARGO_RESERVADO.
 */
const CARGOS_PROPIETARIO = ['propietario', 'propietaria', 'dueño', 'dueña', 'dueno', 'duena'];
const CARGOS_ADMINISTRADOR = ['administrador', 'administradora'];

export function rolDesdeCargo(cargo) {
  const c = texto(cargo).trim().toLowerCase();
  if (CARGOS_PROPIETARIO.includes(c)) return 'Propietario';
  if (CARGOS_ADMINISTRADOR.includes(c)) return 'Administrador';
  return 'Empleado';
}

export const esCargoReservado = (cargo) => rolDesdeCargo(cargo) !== 'Empleado';

export const AVISO_DEL_CARGO =
  'El cargo da el rol en el Desktop: Propietario (o dueño/dueña) y Administrador abren permisos en el PC y solo los asigna el dueño del negocio.';

/** Los negocios donde la persona entra por panel y aun no tiene ficha de operacion: uno por negocio. */
export function negociosSinFicha(p) {
  const conFicha = new Set((p?.llavesOperacion || []).map((o) => o.businessId));
  const vistos = new Set();
  return (p?.membresias || [])
    .filter((m) => m.businessId && !conFicha.has(m.businessId) && !vistos.has(m.businessId) && vistos.add(m.businessId))
    .map((m) => ({ businessId: m.businessId, negocio: m.negocio || m.businessId }));
}

/**
 * Las reglas de FichaDesdePersonaService.Problema, antes del viaje. El PIN vacio no se manda (null): "" seria 400.
 * @returns {{ok: true, cuerpo: {businessId, documento, cargo, pin}} | {ok: false, error: string}}
 */
export function cuerpoDeLaFicha({ businessId, documento, cargo, pin, confirmacion }) {
  const doc = texto(documento).trim();
  const car = texto(cargo).trim();
  const p = texto(pin).trim();
  if (!texto(businessId).trim()) return { ok: false, error: 'Falta el negocio.' };
  if (!doc || doc.length > 30) return { ok: false, error: 'Escribe el documento (hasta 30 caracteres).' };
  if (!car || car.length > 100) return { ok: false, error: 'Escribe el cargo (hasta 100 letras).' };
  if (p && !/^\d{4,6}$/.test(p)) return { ok: false, error: 'El PIN debe tener entre 4 y 6 digitos numericos' };
  if (p && p !== texto(confirmacion).trim()) return { ok: false, error: 'Los PINs no coinciden' };
  return { ok: true, cuerpo: { businessId: texto(businessId).trim(), documento: doc, cargo: car, pin: p || null } };
}

/** Lo que se le dice a quien crea la ficha, por el code de la API (va en details.code; el status en code). */
export function fraseDelResultado(res) {
  if (res?.ok) return { ok: true, frase: 'Ficha de operación creada.' };
  const codigo = texto(res?.details?.code || res?.code);
  const frases = {
    CAMPOS: 'La API no aceptó los datos: ' + texto(res?.details?.error || res?.error),
    CARGO_RESERVADO: 'Ese cargo da rol de dueño o administrador en el PC: solo lo asigna el dueño del negocio. Elige otro cargo.',
    fuera_de_tus_negocios: 'Tu usuario no tiene permiso en ese negocio.',
    PERSONA_NO_EXISTE: 'Esta persona ya no existe en la base. Recarga la ficha.',
    NO_ES_DEL_NEGOCIO: 'La persona ya no entra por panel a ese negocio: no se puede crear su ficha ahí.',
    YA_TIENE_FICHA: 'La persona ya tiene ficha de operación en ese negocio. Recarga la ficha.',
    DOCUMENTO_EN_USO: 'Ese documento ya es de otra ficha de este negocio.'
  };
  return { ok: false, codigo, frase: frases[codigo] || texto(res?.error) || 'No se pudo crear la ficha.', colaboradorId: res?.details?.colaboradorId || null };
}
